import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ComponentProps } from 'react'
import dayjs from 'dayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TablePagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '../../components/BrandState'
import ResizeHandle from '../../components/ResizeHandle'
import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
} from '../../components/dataList'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  FUTURES_ALL_ROWS_PAGE_SIZE,
  useBulkCreateFuturesOrdersMutation,
  useCreateFuturesOrderMutation,
  useDeleteFuturesOrderMutation,
  useGetFuturesOrderFlagsQuery,
  useGetFuturesOrderPartnersQuery,
  useGetFuturesOrderSymbolsQuery,
  useGetFuturesOrderTradeTypesQuery,
  useGetFuturesOrdersQuery,
  useLazyCheckFuturesOrderNoExistsQuery,
  useLazySuggestFuturesOrderNoQuery,
  useUpdateFuturesOrderMutation,
} from '../../store/api/futuresControlApi'
import { futuresApiErrorMessage } from './futuresApiErrorMessage'
import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresSaleContractDialogContentSx,
  futuresSaleContractDialogPaperProps,
  futuresSaleContractDialogTitleSx,
} from './futuresAccessUi'
import { FuturesColumnsMenu } from './FuturesColumnsMenu'
import { useFuturesColumns } from './useFuturesColumns'

const STORAGE_COLS_ORDERS = 'futures.orders'

const BUY_SELL_OPTIONS = ['BUY', 'SELL'] as const

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

// Compact look for the mass-upload grid inputs.
const massCellSx: SxProps<Theme> = { px: 0.5, py: 0.25 }
const massInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { fontSize: '0.72rem', py: 0 },
  '& .MuiInputBase-input': { py: '4px', fontSize: '0.72rem' },
  '& .MuiAutocomplete-input': { py: '2px !important', fontSize: '0.72rem' },
}
const massHeaderSx: SxProps<Theme> = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  fontSize: '0.7rem',
  lineHeight: 1.2,
  py: 0.5,
  px: 0.75,
}

interface Props {
  embedded?: boolean
}

type FuturesGridRow = Record<string, unknown> & { id: string }
type MuiTableProps = ComponentProps<typeof Table>
type MuiTableHeadProps = ComponentProps<typeof TableHead>
type MuiTableBodyProps = ComponentProps<typeof TableBody>
type MuiTableRowProps = ComponentProps<typeof TableRow>
type VirtuosoTableRowProps = MuiTableRowProps & { 'data-item-index'?: number }

type OrderFormModel = {
  orderNumber: string
  filledPrice: string
  lot: string
  budgetedHedge: string
  note: string
  date: string
  buyOrSell: string
  symbol: string
  typeOfTrade: string
  partner: string
  flag: string
}

type MassUploadRow = {
  date: string
  buyOrSell: string
  symbol: string
  filledPrice: string
  typeOfTrade: string
  lot: string
  partner: string
  flag: string
  budgetedHedge: string
  note: string
}

type OrderColumnKind = 'text' | 'date' | 'datetime' | 'number'
type OrderColumnDef = {
  key: string
  label: string
  width?: number
  minWidth?: number
  flex?: number
  kind?: OrderColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

type PaginationModel = {
  page: number
  pageSize: number
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

const todayIso = () => new Date().toISOString().slice(0, 10)

const emptyOrderForm = (): OrderFormModel => ({
  orderNumber: '',
  filledPrice: '',
  lot: '',
  budgetedHedge: '',
  note: '',
  date: todayIso(),
  buyOrSell: '',
  symbol: '',
  typeOfTrade: '',
  partner: '',
  flag: '',
})

const emptyMassRow = (): MassUploadRow => ({
  date: todayIso(),
  buyOrSell: '',
  symbol: '',
  filledPrice: '',
  typeOfTrade: '',
  lot: '',
  partner: '',
  flag: '',
  budgetedHedge: '',
  note: '',
})

// Column order in the Mass upload grid — used for paste/copy mapping.
const MASS_FIELDS: (keyof MassUploadRow)[] = [
  'date',
  'buyOrSell',
  'symbol',
  'filledPrice',
  'typeOfTrade',
  'lot',
  'partner',
  'flag',
  'budgetedHedge',
  'note',
]

// Normalize a pasted date cell to YYYY-MM-DD. Disambiguate D-M vs M-D by value
// when possible; default to D-M-Y (Vietnamese convention) when both <= 12.
// Returns the original string if it cannot be parsed into a valid calendar date,
// so the date input shows it as invalid instead of crashing on bogus ISO.
function buildIsoDate(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  if (year < 1900 || year > 2999) return null
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function parsePastedDate(raw: string): string {
  const s = raw.trim()
  if (!s) return ''

  // YYYY-?-? — accept as ISO, but swap if the second group is clearly the day.
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (iso) {
    const year = parseInt(iso[1], 10)
    const a = parseInt(iso[2], 10)
    const b = parseInt(iso[3], 10)
    const direct = buildIsoDate(year, a, b)
    if (direct) return direct
    const swapped = buildIsoDate(year, b, a) // tolerate YYYY-DD-MM
    if (swapped) return swapped
    return ''
  }

  // d?-m?-y? with /, -, or . as separator.
  const sep = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/)
  if (sep) {
    const p1 = parseInt(sep[1], 10)
    const p2 = parseInt(sep[2], 10)
    let yearStr = sep[3]
    if (yearStr.length === 2) yearStr = `20${yearStr}`
    const year = parseInt(yearStr, 10)

    let day: number, month: number
    if (p1 > 12 && p2 <= 12) {
      day = p1; month = p2 // unambiguous D-M-Y
    } else if (p2 > 12 && p1 <= 12) {
      day = p2; month = p1 // unambiguous M-D-Y
    } else {
      day = p1; month = p2 // ambiguous → default Vietnamese D-M-Y
    }
    const result = buildIsoDate(year, month, day)
    if (result) return result
    return ''
  }

  const t = new Date(s)
  if (!Number.isNaN(t.getTime())) {
    const result = buildIsoDate(t.getFullYear(), t.getMonth() + 1, t.getDate())
    if (result) return result
  }
  return ''
}

// Belt-and-suspenders: only feed YYYY-MM-DD to <input type="date">, otherwise
// React-DOM logs "does not conform to the required format" warnings.
function safeDateValue(v: string): string {
  return /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(v) ? v : ''
}

function normalizeBuySell(raw: string): string {
  const s = raw.trim().toUpperCase()
  if (s === 'B' || s === 'BUY') return 'BUY'
  if (s === 'S' || s === 'SELL') return 'SELL'
  return s
}

/** Strip thousands separators (`,`) + leading/trailing spaces, then verify the result
 *  parses to a finite number. Combobox fields are normalized to the canonical option
 *  (case-insensitive) — values not in the lookup are rejected (empty string), so paste
 *  cannot inject anything the user couldn't pick from the dropdown.
 */
type MassLookupOptions = {
  symbol: string[]
  typeOfTrade: string[]
  partner: string[]
  flag: string[]
}

function normalizeMassNumber(raw: string): string {
  const cleaned = raw.replace(/,/g, '').replace(/\s+/g, '').trim()
  if (cleaned === '') return ''
  const n = Number(cleaned)
  return Number.isFinite(n) ? String(n) : ''
}

function matchOption(raw: string, options: string[]): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  const lower = trimmed.toLowerCase()
  return options.find((opt) => opt.toLowerCase() === lower) ?? ''
}

function coerceMassField(
  field: keyof MassUploadRow,
  raw: string,
  lookups: MassLookupOptions,
): string {
  const v = raw.replace(/\r$/, '')
  if (field === 'date') return parsePastedDate(v)
  if (field === 'buyOrSell') {
    const normalized = normalizeBuySell(v)
    return normalized === 'BUY' || normalized === 'SELL' ? normalized : ''
  }
  if (field === 'symbol') return matchOption(v, lookups.symbol)
  if (field === 'typeOfTrade') return matchOption(v, lookups.typeOfTrade)
  if (field === 'partner') return matchOption(v, lookups.partner)
  if (field === 'flag') return matchOption(v, lookups.flag)
  if (field === 'filledPrice' || field === 'lot' || field === 'budgetedHedge') {
    return normalizeMassNumber(v)
  }
  return v.trim()
}

function parseTsv(text: string): string[][] {
  // Strip a trailing newline so Excel's "copy with newline" doesn't add a blank row.
  const trimmed = text.replace(/\r/g, '').replace(/\n$/, '')
  if (!trimmed) return []
  return trimmed.split('\n').map((line) => line.split('\t'))
}

/** Cột freeze khi scroll ngang — chỉ Order Number. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['orderNumber'])
const FROZEN_FALLBACK_WIDTH: Record<string, number> = {
  orderNumber: 130,
}

const ORDER_COLUMNS: OrderColumnDef[] = [
  { key: 'orderNumber', label: 'Order Number', width: 130 },
  { key: 'date', label: 'Date', width: 110, kind: 'date' },
  { key: 'buyOrSell', label: 'Buy/Sell', width: 90, filter: 'checkboxes' },
  { key: 'symbol', label: 'Symbol', width: 110, filter: 'checkboxes' },
  { key: 'filledPrice', label: 'Filled Price', width: 110, kind: 'number', align: 'right' },
  { key: 'typeOfTrade', label: 'Type of Trade', width: 120, filter: 'checkboxes' },
  { key: 'lot', label: 'Lot', width: 80, kind: 'number', align: 'right' },
  { key: 'partner', label: 'Partner', width: 100, filter: 'checkboxes' },
  { key: 'flag', label: 'Flag', width: 90, filter: 'checkboxes' },
  { key: 'mt', label: 'Mt', width: 80, kind: 'number', align: 'right' },
  { key: 'budgetedHedge', label: 'Budgeted Hedge', width: 130, kind: 'number', align: 'right' },
  { key: 'note', label: 'Note', minWidth: 180, flex: 0.9 },
  { key: 'commodities', label: 'Commodities', minWidth: 150, flex: 0.7 },
  { key: 'cost', label: 'Cost', width: 100, kind: 'number', align: 'right' },
  { key: 'totalCost', label: 'Total Cost', width: 110, kind: 'number', align: 'right' },
  { key: 'value', label: 'Value', width: 100, kind: 'number', align: 'right' },
  { key: 'valueRealized', label: 'Value Realized', width: 120, kind: 'number', align: 'right' },
  { key: 'totalOrders', label: 'Total Orders', width: 120, kind: 'number', align: 'right' },
  { key: 'cumPosition', label: 'Cum. Position', width: 120, kind: 'number', align: 'right' },
  { key: 'lotOpen', label: 'Lot Open', width: 100, kind: 'number', align: 'right' },
  { key: 'lotClosed', label: 'Lot Closed', width: 100, kind: 'number', align: 'right' },
  { key: 'lotWithBs', label: 'Lot with BS', width: 110, kind: 'number', align: 'right' },
  { key: 'allocated', label: 'Allocated', width: 110, kind: 'number', align: 'right' },
  { key: 'market', label: 'Market', width: 100, kind: 'number', align: 'right' },
  { key: 'marketValue', label: 'Market Value', width: 120, kind: 'number', align: 'right' },
  { key: 'm2m', label: 'M2M', width: 95, kind: 'number', align: 'right' },
  { key: 'allocated2', label: 'Allocated2', width: 110, kind: 'number', align: 'right' },
  { key: 'notAllocated', label: 'Not Allocated', width: 120, kind: 'number', align: 'right' },
  { key: 'dateUpdate', label: 'Updated Date', width: 145, kind: 'datetime' },
  { key: 'personUpdate', label: 'Updated Person', width: 130 },
]

function str(v: unknown): string {
  if (v == null || v === '') return ''
  return String(v)
}

function num(v: string): number | undefined {
  if (v.trim() === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

function formatAccessNumber(value: number): string {
  const abs = Math.abs(value)
  const formatted = Number.isInteger(value)
    ? abs.toLocaleString('en-US')
    : abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return value < 0 ? `(${formatted})` : formatted
}

function formatAccessDate(value: unknown): string {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

function formatAccessDateTime(value: unknown): string {
  if (!value) return ''
  const raw = String(value)
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return raw
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

function isNumericLike(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function formatCell(row: Record<string, unknown>, col: OrderColumnDef): string {
  const value = row[col.key]
  if (value == null) return ''
  if (col.kind === 'date') return formatAccessDate(value)
  if (col.kind === 'datetime') return formatAccessDateTime(value)
  if (col.kind === 'number' && isNumericLike(value)) return formatAccessNumber(value)
  if (typeof value === 'number') return formatAccessNumber(value)
  return String(value)
}

function sumNumericRows(rows: Record<string, unknown>[], field: string): number | null {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[field]
    if (typeof value === 'number' && Number.isFinite(value)) {
      total += value
      count += 1
    }
  }
  return count > 0 ? total : null
}

function toPayloadRow(row: MassUploadRow) {
  return {
    date: row.date || undefined,
    buyOrSell: row.buyOrSell || undefined,
    symbol: row.symbol || undefined,
    filledPrice: num(row.filledPrice),
    typeOfTrade: row.typeOfTrade || undefined,
    lot: num(row.lot),
    partner: row.partner || undefined,
    flag: row.flag || undefined,
    budgetedHedge: num(row.budgetedHedge),
    note: row.note || undefined,
  }
}

export default function FuturesOrdersListView({ embedded: _embedded }: Props) {
  void _embedded
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const columnsCtrl = useFuturesColumns({
    mode: 'orders',
    allColumns: ORDER_COLUMNS,
    storageKey: STORAGE_COLS_ORDERS,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl
  const { getWidth, startResize } = useColumnWidths('futures.orders')

  /* ── Filter state (template /table-style) ───────────────────────── */
  const [searchTerm, setSearchTerm] = useState('')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const [pagination, setPagination] = useState<PaginationModel>({ page: 0, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const {
    selectedId: selectedRowId,
    setSelectedId: setSelectedRowId,
    toggleSelect: toggleSelectRow,
  } = useRowSelection<string>()
  const [deleteOrderNo, setDeleteOrderNo] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [creating, setCreating] = useState(true)
  const [form, setForm] = useState<OrderFormModel>(emptyOrderForm)
  const [formError, setFormError] = useState('')
  const [formTouched, setFormTouched] = useState(false)
  const [massOpen, setMassOpen] = useState(false)
  const [massRows, setMassRows] = useState<MassUploadRow[]>([emptyMassRow()])
  const [massSelected, setMassSelected] = useState<Set<number>>(() => new Set())
  // Bumped on full reset / dialog close so all row inputs (especially
  // Autocomplete's uncontrolled inputValue) remount with fresh internal state.
  const [massEpoch, setMassEpoch] = useState(0)
  const suggestSeqRef = useRef(0)

  const symbolLookup = useGetFuturesOrderSymbolsQuery(undefined, { skip: !dialogOpen && !massOpen })
  const tradeTypeLookup = useGetFuturesOrderTradeTypesQuery(undefined, { skip: !dialogOpen && !massOpen })
  const partnerLookup = useGetFuturesOrderPartnersQuery()
  const flagLookup = useGetFuturesOrderFlagsQuery()

  const symbolOptions = symbolLookup.data ?? []
  const tradeTypeOptions = tradeTypeLookup.data ?? []
  const partnerOptions = partnerLookup.data ?? []
  const flagOptions = flagLookup.data ?? []

  const massLookups = useMemo<MassLookupOptions>(
    () => ({
      symbol: symbolLookup.data ?? [],
      typeOfTrade: tradeTypeLookup.data ?? [],
      partner: partnerLookup.data ?? [],
      flag: (flagLookup.data ?? []).map((f) => f.flag),
    }),
    [symbolLookup.data, tradeTypeLookup.data, partnerLookup.data, flagLookup.data],
  )

  const { data, isLoading, isFetching, error, refetch } = useGetFuturesOrdersQuery({
    page: 1,
    pageSize: FUTURES_ALL_ROWS_PAGE_SIZE,
    onlyNotFullyAllocated: false,
  })

  const [fetchSuggestedOrderNo] = useLazySuggestFuturesOrderNoQuery()
  const [triggerCheckOrderNo] = useLazyCheckFuturesOrderNoExistsQuery()
  /* Pre-save check state (skill access-suggest). */
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)
  const handleOrderNoBlur = useCallback(async () => {
    if (!creating) return
    const val = form.orderNumber.trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheckOrderNo({ orderNo: val }).unwrap()
      if (!check.exists) return
      const next = await fetchSuggestedOrderNo(undefined, false).unwrap()
      const newVal = (next.orderNumber || '').trim()
      if (newVal && newVal !== val) {
        setForm((c) => ({ ...c, orderNumber: newVal }))
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      // BE sẽ raise lúc Save nếu thật sự trùng.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }, [creating, form.orderNumber, triggerCheckOrderNo, fetchSuggestedOrderNo])
  const [createOrder, createState] = useCreateFuturesOrderMutation()
  const [updateOrder, updateState] = useUpdateFuturesOrderMutation()
  const [deleteOrder, deleteState] = useDeleteFuturesOrderMutation()
  const [bulkOrders, bulkState] = useBulkCreateFuturesOrdersMutation()

  const baseRows = useMemo(
    (): FuturesGridRow[] =>
      (data?.items ?? []).map((r, idx) => ({
        ...r,
        id: `${idx}-${String(r.orderNumber ?? '')}-${String(r.date ?? '')}`,
      })),
    [data?.items],
  )

  /* ── Filter pipeline: search ∧ per-column ▽ ─────────────────────── */
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, ORDER_COLUMNS) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters],
  )

  /* ── Pagination clamp ───────────────────────────────────────────── */
  const displayedRows = useMemo((): FuturesGridRow[] => {
    if (pagination.pageSize === FUTURES_ALL_ROWS_PAGE_SIZE) return filteredRows
    const start = pagination.page * pagination.pageSize
    return filteredRows.slice(start, start + pagination.pageSize)
  }, [filteredRows, pagination.page, pagination.pageSize])

  // Alias để giữ tương thích các nơi tham chiếu `rows` cũ.
  const rows = displayedRows

  /* ── Per-column popover meta ────────────────────────────────────── */
  const popoverColumn = useMemo(
    () =>
      colPopover
        ? ORDER_COLUMNS.find((c) => c.key === colPopover.key) ?? null
        : null,
    [colPopover],
  )
  const popoverKind = useMemo(
    () =>
      popoverColumn
        ? inferColumnFilterKind(popoverColumn as never, baseRows)
        : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const selectedRow = useMemo((): FuturesGridRow | null => {
    if (!selectedRowId) return null
    const rid = selectedRowId
    return baseRows.find((r) => r.id === rid) ?? null
  }, [selectedRowId, baseRows])

  const totalRowValues = useMemo(
    () => new Map(visibleColumns.map((col) => [col.key, sumNumericRows(displayedRows, col.key)] as const)),
    [displayedRows, visibleColumns],
  )

  const minTableWidth = visibleColumns.length * 96
  const gridKey = `${pagination.page}:${pagination.pageSize}:${displayedRows.length}`


  const openNew = useCallback(() => {
    const seq = ++suggestSeqRef.current
    setCreating(true)
    setSelectedRowId(null)
    setForm(emptyOrderForm())
    setFormError('')
    setFormTouched(false)
    setDialogOpen(true)
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    void fetchSuggestedOrderNo()
      .unwrap()
      .then((res) => {
        if (seq !== suggestSeqRef.current) return
        setForm((current) => (current.orderNumber.trim() ? current : { ...current, orderNumber: res.orderNumber }))
      })
      .catch(() => {
        /* keep dialog open even if suggestion service is briefly unavailable */
      })
  }, [fetchSuggestedOrderNo])

  const openEdit = useCallback(() => {
    const row = selectedRow
    if (!row) {
      snack.warning('Please choose a record!')
      return
    }
    const orderNumber = str(row.orderNumber)
    if (orderNumber.startsWith('SP')) {
      const spreadsCode = orderNumber.replace(/.{3}$/, '')
      snack.warning(`Please update ${spreadsCode} in tab Spreads!`)
      return
    }
    setCreating(false)
    setFormError('')
    setFormTouched(false)
    setForm({
      orderNumber,
      filledPrice: str(row.filledPrice ?? ''),
      lot: str(row.lot ?? ''),
      budgetedHedge: str(row.budgetedHedge ?? ''),
      note: str(row.note ?? ''),
      date: str(row.date).slice(0, 10) || todayIso(),
      buyOrSell: str(row.buyOrSell ?? ''),
      symbol: str(row.symbol ?? ''),
      typeOfTrade: str(row.typeOfTrade ?? ''),
      partner: str(row.partner ?? ''),
      flag: str(row.flag ?? ''),
    })
    setDialogOpen(true)
  }, [selectedRow, snack])

  const openDelete = useCallback(() => {
    const row = selectedRow
    if (!row?.orderNumber) {
      snack.warning('Please choose a record!')
      return
    }
    const orderNumber = String(row.orderNumber)
    if (orderNumber.startsWith('SP')) {
      const spreadsCode = orderNumber.replace(/.{3}$/, '')
      snack.warning(`Please delete ${spreadsCode} in tab Spreads!`)
      return
    }
    // Allocation guard (DEL-002): Allocated2 cộng tất cả allocation active trên
    // order này. Nếu > 0 → chặn ngay trước khi mở confirm dialog (backend cũng
    // re-check trong delete_orders_futures_soft phòng race condition).
    const allocated2Raw = row.allocated2
    const allocated2 =
      typeof allocated2Raw === 'number'
        ? allocated2Raw
        : allocated2Raw == null || allocated2Raw === ''
          ? 0
          : Number(allocated2Raw)
    if (Number.isFinite(allocated2) && allocated2 > 0) {
      snack.warning(
        `Order has active allocations — cannot delete '${orderNumber}' while ${allocated2} lot allocation(s) remain. Please remove the allocation first.`,
      )
      return
    }
    setDeleteOrderNo(orderNumber)
  }, [selectedRow, snack])

  /** Mirror VBA `btnSave_Click` validation rules from `frmFuturesQryFuturesOrderAdd`. */
  const validateForm = (): string => {
    const orderNumber = form.orderNumber.trim()
    if (!orderNumber) return 'Please enter valid Order Number!'
    if (creating && !orderNumber.toUpperCase().startsWith('FU-')) {
      return "Please type Order Number with format 'FU-*' !"
    }
    if (!form.date) return 'Please choose Date!'
    if (!form.buyOrSell.trim()) return 'Please choose Buy/Sell!'
    const sym = form.symbol.trim()
    if (!sym) return 'Please choose Symbol!'
    if (!symbolOptions.includes(sym)) return 'Please choose Symbol from list!'
    const tt = form.typeOfTrade.trim()
    if (!tt) return 'Please choose Type of trade!'
    if (!tradeTypeOptions.includes(tt)) return 'Please choose Type of trade from list!'
    if (!form.partner.trim()) return 'Please choose Partner!'
    if (!form.flag.trim()) return 'Please choose flag!'
    // Allocation-vs-lot guard is enforced server-side in `update_orders_futures`
    // because the rule is side-aware (BUY checks Sale allocations, SELL checks
    // Purchase allocations) and the order row only carries the total allocated
    // figure — a FE pre-check using the total would over-block valid edits when
    // cross-side allocations exist. BE error surfaces via futuresApiErrorMessage.
    return ''
  }

  // Per-field error flags for red highlight (mirrors LocalPFormDialog).
  const formFieldErrors = useMemo(() => {
    const on = form.orderNumber.trim()
    const sym = form.symbol.trim()
    const tt = form.typeOfTrade.trim()
    return {
      orderNumber: !on || (creating && !on.toUpperCase().startsWith('FU-')),
      date: !form.date,
      buyOrSell: !form.buyOrSell.trim() || !(BUY_SELL_OPTIONS as readonly string[]).includes(form.buyOrSell.trim()),
      symbol: !sym || !symbolOptions.includes(sym),
      typeOfTrade: !tt || !tradeTypeOptions.includes(tt),
      partner: !form.partner.trim() || !partnerOptions.includes(form.partner.trim()),
      flag: !form.flag.trim() || !flagOptions.some((f) => f.flag === form.flag.trim()),
    }
  }, [form, creating, symbolOptions, tradeTypeOptions, partnerOptions, flagOptions])

  const submit = async () => {
    if (precheckBusyRef.current) return
    setFormTouched(true)
    const err = validateForm()
    if (err) {
      setFormError(err)
      return
    }
    setFormError('')
    try {
      const payload = {
        date: form.date || undefined,
        buyOrSell: form.buyOrSell || undefined,
        symbol: form.symbol || undefined,
        filledPrice: num(form.filledPrice),
        typeOfTrade: form.typeOfTrade || undefined,
        lot: num(form.lot),
        partner: form.partner || undefined,
        flag: form.flag || undefined,
        budgetedHedge: num(form.budgetedHedge),
        note: form.note || undefined,
      }

      if (creating) {
        await createOrder({
          orderNumber: form.orderNumber.trim(),
          ...payload,
        }).unwrap()
      } else {
        await updateOrder({
          orderNumber: form.orderNumber.trim(),
          body: payload,
        }).unwrap()
      }
      snack.success(creating ? 'Successfully Added' : 'Successfully Updated')
      setDialogOpen(false)
      setSelectedRowId(null)
    } catch (err2) {
      snack.error(futuresApiErrorMessage(err2))
    }
  }

  const confirmRemoveOrder = async () => {
    if (!deleteOrderNo) return
    try {
      await deleteOrder(deleteOrderNo).unwrap()
      snack.success('Successfully Deleted')
      setSelectedRowId(null)
      setDeleteOrderNo(null)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const updateMassRow = useCallback((index: number, field: keyof MassUploadRow, value: string) => {
    setMassRows((current) => current.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)))
  }, [])

  // Excel-like paste: always intercept so coercion runs even for a single cell
  // (Excel may copy "5/15/2026" with no tab/newline). Multi-cell payloads grow
  // the grid automatically. Pasting into Note preserves newlines as part of the
  // value instead of splitting into rows.
  const NOTE_FIELD_INDEX = MASS_FIELDS.indexOf('note')
  const handleMassPaste = useCallback(
    (rowIdx: number, colIdx: number, event: React.ClipboardEvent<HTMLElement>) => {
      const text = event.clipboardData?.getData('text') ?? ''
      if (!text) return
      // If pasting directly into Note, treat the whole payload as the note body.
      if (colIdx === NOTE_FIELD_INDEX) {
        if (!text.includes('\t') && !text.includes('\n')) return
        event.preventDefault()
        event.stopPropagation()
        setMassRows((current) =>
          current.map((row, idx) =>
            idx === rowIdx ? { ...row, note: text.replace(/\r\n/g, '\n').replace(/\n$/, '') } : row,
          ),
        )
        return
      }
      event.preventDefault()
      event.stopPropagation()
      const matrix = parseTsv(text)
      if (matrix.length === 0) return
      setMassRows((current) => {
        const next = current.map((row) => ({ ...row }))
        while (next.length < rowIdx + matrix.length) {
          next.push(emptyMassRow())
        }
        matrix.forEach((cells, r) => {
          const targetRow = next[rowIdx + r]
          cells.forEach((rawCell, c) => {
            const fieldIdx = colIdx + c
            if (fieldIdx >= MASS_FIELDS.length) return
            const field = MASS_FIELDS[fieldIdx]
            targetRow[field] = coerceMassField(field, rawCell, massLookups)
          })
        })
        return next
      })
    },
    [NOTE_FIELD_INDEX, massLookups],
  )

  const toggleMassRowSelected = useCallback((index: number) => {
    setMassSelected((current) => {
      const next = new Set(current)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }, [])

  const toggleMassSelectAll = useCallback(() => {
    setMassSelected((current) => {
      if (current.size === massRows.length && massRows.length > 0) return new Set()
      return new Set(massRows.map((_, idx) => idx))
    })
  }, [massRows])

  const formatMassCellForCopy = (row: MassUploadRow, field: keyof MassUploadRow): string => {
    const value = row[field] ?? ''
    return String(value).replace(/[\t\r\n]/g, ' ')
  }

  const copyMassRows = useCallback(async () => {
    const indices = massSelected.size > 0
      ? Array.from(massSelected).sort((a, b) => a - b)
      : massRows.map((_, idx) => idx)
    if (indices.length === 0) {
      snack.warning('No rows to copy')
      return
    }
    const escapeHtml = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const tsv = indices
      .map((idx) => MASS_FIELDS.map((f) => formatMassCellForCopy(massRows[idx], f)).join('\t'))
      .join('\n')
    const html =
      '<table>' +
      indices
        .map(
          (idx) =>
            '<tr>' +
            MASS_FIELDS.map(
              (f) => `<td>${escapeHtml(formatMassCellForCopy(massRows[idx], f))}</td>`,
            ).join('') +
            '</tr>',
        )
        .join('') +
      '</table>'

    // Selection-based execCommand on a contenteditable div. This is the most
    // Excel-friendly path because it puts BOTH text/html and text/plain on the
    // clipboard, works inside MUI dialogs (uses Selection, not focus), and runs
    // on plain HTTP origins where navigator.clipboard is undefined.
    const copyViaSelection = (): boolean => {
      const div = document.createElement('div')
      div.contentEditable = 'true'
      div.innerHTML = html
      div.style.position = 'fixed'
      div.style.top = '-9999px'
      div.style.left = '-9999px'
      div.style.opacity = '0'
      document.body.appendChild(div)
      const selection = window.getSelection()
      if (!selection) {
        document.body.removeChild(div)
        return false
      }
      const savedRanges: Range[] = []
      for (let i = 0; i < selection.rangeCount; i++) savedRanges.push(selection.getRangeAt(i).cloneRange())
      const range = document.createRange()
      range.selectNodeContents(div)
      selection.removeAllRanges()
      selection.addRange(range)
      let ok = false
      try {
        ok = document.execCommand('copy')
      } catch {
        ok = false
      }
      selection.removeAllRanges()
      savedRanges.forEach((r) => selection.addRange(r))
      document.body.removeChild(div)
      return ok
    }

    const copyViaTextarea = (): boolean => {
      const ta = document.createElement('textarea')
      ta.value = tsv
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.top = '-9999px'
      document.body.appendChild(ta)
      const prevActive = document.activeElement as HTMLElement | null
      ta.focus()
      ta.select()
      let ok = false
      try {
        ok = document.execCommand('copy')
      } catch {
        ok = false
      }
      document.body.removeChild(ta)
      prevActive?.focus?.()
      return ok
    }

    let ok = false
    try {
      const ClipboardItemCtor = (window as unknown as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem
      if (ClipboardItemCtor && navigator.clipboard?.write) {
        const item = new ClipboardItemCtor({
          'text/plain': new Blob([tsv], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        })
        await navigator.clipboard.write([item])
        ok = true
      }
    } catch {
      ok = false
    }
    if (!ok) ok = copyViaSelection()
    if (!ok) {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(tsv)
          ok = true
        }
      } catch {
        ok = false
      }
    }
    if (!ok) ok = copyViaTextarea()

    if (ok) {
      snack.success(`Copied ${indices.length} row(s) — paste into Excel with Ctrl+V`)
    } else {
      snack.error('Browser blocked clipboard access. Open the page over HTTPS or localhost.')
    }
  }, [massSelected, massRows, snack])

  const deleteSelectedMassRows = useCallback(() => {
    if (massSelected.size === 0) {
      snack.warning('Select rows to delete')
      return
    }
    setMassRows((current) => {
      const filtered = current.filter((_, idx) => !massSelected.has(idx))
      return filtered.length === 0 ? [emptyMassRow()] : filtered
    })
    setMassSelected(new Set())
  }, [massSelected, snack])

  const runMassUpload = async () => {
    if (massRows.length === 0) {
      snack.warning('At least 1 row is required')
      return
    }
    const inList = (value: string, list: string[]): boolean =>
      list.some((opt) => opt.toLowerCase() === value.trim().toLowerCase())
    for (const [index, row] of massRows.entries()) {
      const idx = index + 1
      if (!row.date.trim()) { snack.warning(`Row ${idx}: missing Date`); return }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(row.date.trim())) { snack.warning(`Row ${idx}: invalid Date`); return }
      const bs = row.buyOrSell.trim().toUpperCase()
      if (bs !== 'BUY' && bs !== 'SELL') { snack.warning(`Row ${idx}: Buy/Sell must be BUY or SELL`); return }
      if (!row.symbol.trim()) { snack.warning(`Row ${idx}: missing Symbol`); return }
      if (massLookups.symbol.length && !inList(row.symbol, massLookups.symbol)) {
        snack.warning(`Row ${idx}: Symbol not in list`); return
      }
      const filledPrice = num(row.filledPrice)
      if (filledPrice == null || filledPrice <= 0) { snack.warning(`Row ${idx}: Filled Price > 0`); return }
      if (!row.typeOfTrade.trim()) { snack.warning(`Row ${idx}: missing Type`); return }
      if (massLookups.typeOfTrade.length && !inList(row.typeOfTrade, massLookups.typeOfTrade)) {
        snack.warning(`Row ${idx}: Type not in list`); return
      }
      const lot = num(row.lot)
      if (lot == null || lot <= 0) { snack.warning(`Row ${idx}: Lot > 0`); return }
      if (!row.partner.trim()) { snack.warning(`Row ${idx}: missing Partner`); return }
      if (massLookups.partner.length && !inList(row.partner, massLookups.partner)) {
        snack.warning(`Row ${idx}: Partner not in list`); return
      }
      if (!row.flag.trim()) { snack.warning(`Row ${idx}: missing Flag`); return }
      if (massLookups.flag.length && !inList(row.flag, massLookups.flag)) {
        snack.warning(`Row ${idx}: Flag not in list`); return
      }
      if (row.budgetedHedge.trim() !== '' && num(row.budgetedHedge) == null) {
        snack.warning(`Row ${idx}: invalid Budgeted Hedge`)
        return
      }
    }

    try {
      const payloadRows = massRows.map((row) => toPayloadRow(row))
      const result = await bulkOrders({ rows: payloadRows }).unwrap()
      snack.success(`Created ${result.created}/${massRows.length} rows`)
      setMassOpen(false)
      setMassRows([emptyMassRow()])
      setMassSelected(new Set())
      setMassEpoch((n) => n + 1)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  useEffect(() => {
    if (massOpen && massRows.length === 0) {
      const timer = window.setTimeout(() => setMassRows([emptyMassRow()]), 0)
      return () => window.clearTimeout(timer)
    }
  }, [massOpen, massRows.length])

  const resetMassForm = useCallback(() => {
    setMassRows([emptyMassRow()])
    setMassSelected(new Set())
    setMassEpoch((n) => n + 1)
  }, [])

  const closeMassDialog = useCallback(() => {
    setMassOpen(false)
    setMassSelected(new Set())
    setMassEpoch((n) => n + 1)
  }, [])

  const busy = createState.isLoading || updateState.isLoading || deleteState.isLoading || bulkState.isLoading
  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPagination((p) => ({ ...p, page: 0 }))
    setSelectedRowId(null)
    filterPopover.close()
  }

  return (
    <>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <FilterBar
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
          rightSlot={
            canEdit ? (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} onClick={openNew} startIcon={<AddIcon sx={{ fontSize: 16 }} />} title="New">
                  <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
                </Button>
                <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow} onClick={openEdit} startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} title="Edit">
                  <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  sx={actionToolbarBtnSx}
                  disabled={!selectedRow}
                  onClick={openDelete}
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                  title="Delete"
                >
                  <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
                </Button>
                <Button variant="outlined" size="small" sx={actionToolbarBtnSx} onClick={() => setMassOpen(true)} startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />} title="Mass upload">
                  <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Mass upload</Box>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline', md: 'none' } }}>Mass</Box>
                </Button>
              </Stack>
            ) : null
          }
        >
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openColMenu(e.currentTarget)}
            sx={filterBarBtnSx}
            title="Columns"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
          </Button>
        </FilterBar>

        <Divider sx={{ mb: 0.5 }} />

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v)
                setPagination((p) => ({ ...p, page: 0 }))
              }}
              placeholder="Order, symbol, partner…"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPagination((p) => ({ ...p, page: 0 }))
          }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <Box className="app-dataframe">
          <Box
            className="app-dataframe-toolbar"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
              flexWrap: 'wrap',
              minHeight: 24,
              py: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
              {(isLoading || isFetching) && <CircularProgress size={15} />}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Total: {filteredRows.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Loaded: {baseRows.length}
              </Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={pagination.pageSize === FUTURES_ALL_ROWS_PAGE_SIZE ? 0 : pagination.page}
                onPageChange={(_, newPage) => {
                  setPagination((p) => ({ ...p, page: newPage }))
                  setSelectedRowId(null)
                }}
                rowsPerPage={pagination.pageSize}
                onRowsPerPageChange={(e) => {
                  setPagination({ page: 0, pageSize: Number(e.target.value) })
                  setSelectedRowId(null)
                }}
                rowsPerPageOptions={[25, 50, 100, { label: 'All', value: FUTURES_ALL_ROWS_PAGE_SIZE }]}
                labelRowsPerPage="Rows"
                sx={TABLE_PAGINATION_COMPACT}
              />
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
            {error ? (
              <BrandState
                tone="error"
                title="Could not load Futures Orders data"
                description="Please try again."
                action={
                  <Button size="small" variant="outlined" onClick={() => void refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : isLoading && rows.length === 0 ? (
              <BrandState tone="loading" title="Loading…" description="Please wait." />
            ) : displayedRows.length === 0 ? (
              <BrandState
                title="No data"
                description={
                  hasActiveFilters
                    ? 'No rows match the filter on the current page. Try clearing the filter or changing pages.'
                    : 'No orders yet.'
                }
              />
            ) : (
              <TableVirtuoso
                key={gridKey}
                style={{ height: '100%' }}
                data={displayedRows}
                fixedHeaderContent={() => (
                  <TableRow>
                    {visibleColumns.map((col) => {
                      const isFrozen = FROZEN_KEYS.has(col.key)
                      const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                      const headerStyle: CSSProperties = {}
                      if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                      if (isFrozen) {
                        headerStyle.position = 'sticky'
                        headerStyle.top = 0
                        headerStyle.left = 0
                        headerStyle.zIndex = 100
                      }
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className="app-table-header-cell"
                          style={Object.keys(headerStyle).length ? headerStyle : undefined}
                          sx={{
                            whiteSpace: 'nowrap',
                            userSelect: 'none',
                            ...(isFrozen ? {} : { position: 'relative' }),
                          }}
                        >
                          {col.label}
                          <ColumnFilterIcon
                            active={columnFilters.isActive(col.key)}
                            onClick={(position) =>
                              setColPopover({ key: col.key, position })
                            }
                            ariaLabel={`Filter ${col.label}`}
                          />
                          <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                fixedFooterContent={() => (
                  <TableRow>
                    {visibleColumns.map((col, index) => {
                      const isFrozen = FROZEN_KEYS.has(col.key)
                      const total = totalRowValues.get(col.key)
                      const value = total == null ? '' : formatCell({ [col.key]: total }, col)
                      const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col' : undefined}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            py: 0.5,
                            bgcolor: 'background.paper',
                            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {index === 0 ? 'Total' : value}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                itemContent={(_: number, row: FuturesGridRow) => {
                  const notAllocatedValue = row.notAllocated
                  const highlightOrderNumber =
                    typeof notAllocatedValue === 'number' &&
                    Number.isFinite(notAllocatedValue) &&
                    notAllocatedValue > 0
                  return visibleColumns.map((col) => {
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const value = formatCell(row, col)
                    const isHighlighted = col.key === 'orderNumber' && highlightOrderNumber
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          ...(isHighlighted ? { color: '#ff0000', fontWeight: 600 } : null),
                        }}
                      >
                        <Box component="span" className="app-table-cell-text" title={value || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {value || '-'}
                        </Box>
                      </TableCell>
                    )
                  })
                }}
                components={{
                  Table: (props: MuiTableProps) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                  TableHead: (props: MuiTableHeadProps) => <TableHead {...props} component="thead" />,
                  TableBody: (props: MuiTableBodyProps) => <TableBody {...props} component="tbody" />,
                  TableRow: (props: VirtuosoTableRowProps) => {
                    const idx = Number(props['data-item-index'])
                    const row = Number.isFinite(idx) ? displayedRows[idx] : null
                    const rowId = row?.id ?? null
                    return (
                      <TableRow
                        {...props}
                        hover
                        selected={rowId != null && rowId === selectedRowId}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (rowId != null) toggleSelectRow(rowId)
                        }}
                      />
                    )
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={deleteOrderNo != null}
        onClose={() => setDeleteOrderNo(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={futuresSaleContractDialogPaperProps}
      >
        <DialogTitle sx={futuresSaleContractDialogTitleSx}>
          <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0 }}>Delete Futures Order</Box>
          <Box sx={futuresFormDialogActionsBoxSx}>
            <Button
              variant="contained"
              color="error"
              size="small"
              disabled={deleteState.isLoading}
              onClick={() => void confirmRemoveOrder()}
              sx={futuresFormDialogActionBtnSx}
            >
              {deleteState.isLoading ? <CircularProgress size={14} color="inherit" /> : 'Delete'}
            </Button>
            <Button variant="outlined" size="small" onClick={() => setDeleteOrderNo(null)} sx={futuresFormDialogActionBtnSx}>
              Cancel
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={futuresSaleContractDialogContentSx}>
          <Typography variant="body2">
            {deleteOrderNo ? `Do you want to delete Futures order '${deleteOrderNo}'?` : ''}
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={futuresSaleContractDialogPaperProps}
      >
        <DialogTitle sx={futuresSaleContractDialogTitleSx}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
            <Box component="span">{creating ? 'Add Futures Order' : 'Edit Futures Order'}</Box>
            <Box sx={futuresFormDialogActionsBoxSx}>
              <Button
                variant="contained"
                size="small"
                color="success"
                disabled={busy}
                onClick={() => void submit()}
                sx={futuresFormDialogActionBtnSx}
              >
                {busy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setDialogOpen(false)}
                sx={futuresFormDialogActionBtnSx}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={futuresSaleContractDialogContentSx}>
          {regenNotice ? (
            <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>
              Order Number <b>{regenNotice.from}</b> was just created —
              suggested new code <b>{regenNotice.to}</b>.
            </Alert>
          ) : null}
          {formError ? (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {formError}
            </Typography>
          ) : null}
          <Box sx={{ maxWidth: 980, mx: 'auto', pb: 1.5 }}>
            <Grid container spacing={0.75}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Order Number *"
                  size="small"
                  fullWidth
                  value={form.orderNumber}
                  onChange={(e) => setForm((c) => ({ ...c, orderNumber: e.target.value }))}
                  onBlur={() => void handleOrderNoBlur()}
                  InputProps={{ readOnly: !creating }}
                  error={formTouched && formFieldErrors.orderNumber}
                  helperText={
                    precheckBusy
                      ? 'Verifying order number…'
                      : formTouched && formFieldErrors.orderNumber
                        ? "Please type Order Number with format 'FU-*'"
                        : creating
                          ? "Format 'FU-*'"
                          : 'Read-only on edit'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Date *"
                  format="DD-MMM-YYYY"
                  value={form.date ? dayjs(form.date) : null}
                  onChange={(d) => setForm((c) => ({ ...c, date: d && d.isValid() ? d.format('YYYY-MM-DD') : '' }))}
                  slotProps={{ textField: {
                    size: 'small',
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    error: formTouched && formFieldErrors.date,
                    helperText: formTouched && formFieldErrors.date ? 'Please choose Date' : undefined,
                  } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  autoHighlight
                  options={[...BUY_SELL_OPTIONS]}
                  value={form.buyOrSell || null}
                  onChange={(_, v) => setForm((c) => ({ ...c, buyOrSell: v ?? '' }))}
                  isOptionEqualToValue={(opt, val) => opt === val}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buy/Sell *"
                      error={formTouched && formFieldErrors.buyOrSell}
                      helperText={formTouched && formFieldErrors.buyOrSell ? 'Please choose Buy/Sell from list' : undefined}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  autoHighlight
                  options={symbolOptions}
                  value={form.symbol || null}
                  onChange={(_, v) => setForm((c) => ({ ...c, symbol: v ?? '' }))}
                  isOptionEqualToValue={(opt, val) => opt === val}
                  loading={symbolLookup.isLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Symbol *"
                      error={formTouched && formFieldErrors.symbol}
                      helperText={formTouched && formFieldErrors.symbol ? 'Please choose Symbol from list' : undefined}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  autoHighlight
                  options={tradeTypeOptions}
                  value={form.typeOfTrade || null}
                  onChange={(_, v) => setForm((c) => ({ ...c, typeOfTrade: v ?? '' }))}
                  isOptionEqualToValue={(opt, val) => opt === val}
                  loading={tradeTypeLookup.isLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Type of Trade *"
                      error={formTouched && formFieldErrors.typeOfTrade}
                      helperText={formTouched && formFieldErrors.typeOfTrade ? 'Please choose Type of Trade from list' : undefined}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Lot"
                  type="number"
                  size="small"
                  fullWidth
                  value={form.lot}
                  onChange={(e) => setForm((c) => ({ ...c, lot: e.target.value }))}
                  inputProps={{ inputMode: 'decimal', step: 'any' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Filled Price"
                  type="number"
                  size="small"
                  fullWidth
                  value={form.filledPrice}
                  onChange={(e) => setForm((c) => ({ ...c, filledPrice: e.target.value }))}
                  inputProps={{ inputMode: 'decimal', step: 'any' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  autoHighlight
                  options={partnerOptions}
                  value={form.partner || null}
                  onChange={(_, v) => setForm((c) => ({ ...c, partner: v ?? '' }))}
                  isOptionEqualToValue={(opt, val) => opt === val}
                  loading={partnerLookup.isLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Partner *"
                      error={formTouched && formFieldErrors.partner}
                      helperText={formTouched && formFieldErrors.partner ? 'Please choose Partner from list' : undefined}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  autoHighlight
                  options={flagOptions}
                  getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.flag)}
                  isOptionEqualToValue={(opt, v) =>
                    (typeof opt === 'string' ? opt : opt.flag) === (typeof v === 'string' ? v : v?.flag)
                  }
                  value={form.flag ? flagOptions.find((o) => o.flag === form.flag) ?? null : null}
                  onChange={(_, v) => setForm((c) => ({ ...c, flag: typeof v === 'string' ? v : v?.flag ?? '' }))}
                  loading={flagLookup.isLoading}
                  renderOption={(props, opt) => (
                    <li {...props} key={opt.flag}>
                      <Box>
                        <Typography variant="body2">{opt.flag}</Typography>
                        {opt.description ? (
                          <Typography variant="caption" color="text.secondary">
                            {opt.description}
                          </Typography>
                        ) : null}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Flag *"
                      error={formTouched && formFieldErrors.flag}
                      helperText={formTouched && formFieldErrors.flag ? 'Please choose Flag from list' : undefined}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Budgeted Hedge"
                  type="number"
                  size="small"
                  fullWidth
                  value={form.budgetedHedge}
                  onChange={(e) => setForm((c) => ({ ...c, budgetedHedge: e.target.value }))}
                  inputProps={{ inputMode: 'decimal', step: 'any' }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Note"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.note}
                  onChange={(e) => setForm((c) => ({ ...c, note: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={massOpen}
        onClose={closeMassDialog}
        fullWidth
        maxWidth="xl"
        scroll="paper"
        PaperProps={futuresSaleContractDialogPaperProps}
      >
        <DialogTitle sx={futuresSaleContractDialogTitleSx}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
            <Box component="span">Mass upload futures orders</Box>
            <Box sx={futuresFormDialogActionsBoxSx}>
              <Button
                variant="contained"
                size="small"
                color="success"
                disabled={bulkState.isLoading || massRows.length === 0}
                onClick={() => void runMassUpload()}
                sx={futuresFormDialogActionBtnSx}
              >
                {bulkState.isLoading ? <CircularProgress size={14} color="inherit" /> : 'Upload'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={closeMassDialog}
                sx={futuresFormDialogActionBtnSx}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={futuresSaleContractDialogContentSx}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                
              </Typography>
              <Stack direction="row" spacing={0.75}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setMassRows((current) => [...current, emptyMassRow()])}
                  sx={{ fontSize: '0.7rem', py: 0.25, px: 0.875, minHeight: 26, textTransform: 'none' }}
                >
                  Add row
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                  onClick={() => void copyMassRows()}
                  sx={{ fontSize: '0.7rem', py: 0.25, px: 0.875, minHeight: 26, textTransform: 'none' }}
                >
                  {massSelected.size > 0 ? `Copy (${massSelected.size})` : 'Copy all'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                  disabled={massSelected.size === 0}
                  onClick={deleteSelectedMassRows}
                  sx={{ fontSize: '0.7rem', py: 0.25, px: 0.875, minHeight: 26, textTransform: 'none' }}
                >
                  {massSelected.size > 0 ? `Delete (${massSelected.size})` : 'Delete selected'}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={resetMassForm}
                  sx={{ fontSize: '0.7rem', py: 0.25, px: 0.875, minHeight: 26, textTransform: 'none' }}
                >
                  Reset
                </Button>
              </Stack>
            </Stack>

            <Box sx={{ maxHeight: '55vh', overflow: 'auto', border: '1px solid #bdbdbd', bgcolor: '#fff' }}>
              <Table size="small" stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      align="left"
                      sx={{ bgcolor: '#fafafa', width: 44, pl: 0.75, pr: 2 }}
                    >
                      <Checkbox
                        size="small"
                        indeterminate={massSelected.size > 0 && massSelected.size < massRows.length}
                        checked={massRows.length > 0 && massSelected.size === massRows.length}
                        onChange={toggleMassSelectAll}
                        sx={{ p: 0.25, ml: 0 }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 118, pl: 1.25 }}>Date *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 90 }}>Buy/Sell *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 110 }}>Symbol *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 95 }}>Filled Price *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 110 }}>Type of Trade *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 70 }}>Lot *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 100 }}>Partner *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 100 }}>Flag *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 105 }}>Budgeted Hedge</TableCell>
                    <TableCell sx={massHeaderSx}>Note</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 40 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {massRows.map((row, index) => {
                    const selected = massSelected.has(index)
                    return (
                      <TableRow key={`${massEpoch}-${index}`} hover selected={selected}>
                        <TableCell
                          padding="checkbox"
                          align="left"
                          sx={{ width: 44, pl: 0.75, pr: 2 }}
                        >
                          <Checkbox
                            size="small"
                            checked={selected}
                            onChange={() => toggleMassRowSelected(index)}
                            sx={{ p: 0.25, ml: 0 }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 128, pl: 1.25 }} onPasteCapture={(e) => handleMassPaste(index, 0, e)}>
                          <TextField
                            size="small"
                            type="date"
                            value={safeDateValue(row.date)}
                            onChange={(e) => updateMassRow(index, 'date', e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={massInputSx}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 100 }} onPasteCapture={(e) => handleMassPaste(index, 1, e)}>
                          <Autocomplete
                            size="small"
                            options={[...BUY_SELL_OPTIONS]}
                            value={row.buyOrSell || null}
                            onChange={(_, v) => updateMassRow(index, 'buyOrSell', v ?? '')}
                            onInputChange={(_, v, reason) => {
                              if (reason === 'input') updateMassRow(index, 'buyOrSell', v.toUpperCase())
                            }}
                            freeSolo
                            renderInput={(params) => <TextField {...params} placeholder="BUY / SELL" sx={massInputSx} />}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 130 }} onPasteCapture={(e) => handleMassPaste(index, 2, e)}>
                          <Autocomplete
                            size="small"
                            options={symbolOptions}
                            value={row.symbol || null}
                            onChange={(_, v) => updateMassRow(index, 'symbol', v ?? '')}
                            onInputChange={(_, v, reason) => {
                              if (reason === 'input') updateMassRow(index, 'symbol', v)
                            }}
                            freeSolo
                            loading={symbolLookup.isLoading}
                            renderInput={(params) => <TextField {...params} placeholder="Symbol" sx={massInputSx} />}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 110 }} onPasteCapture={(e) => handleMassPaste(index, 3, e)}>
                          <TextField
                            size="small"
                            type="number"
                            value={row.filledPrice}
                            onChange={(e) => updateMassRow(index, 'filledPrice', e.target.value)}
                            onBlur={(e) => updateMassRow(index, 'filledPrice', normalizeMassNumber(e.target.value))}
                            fullWidth
                            inputProps={{ inputMode: 'decimal', step: 'any', style: { textAlign: 'right' } }}
                            sx={massInputSx}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 140 }} onPasteCapture={(e) => handleMassPaste(index, 4, e)}>
                          <Autocomplete
                            size="small"
                            options={tradeTypeOptions}
                            value={row.typeOfTrade || null}
                            onChange={(_, v) => updateMassRow(index, 'typeOfTrade', v ?? '')}
                            onInputChange={(_, v, reason) => {
                              if (reason === 'input') updateMassRow(index, 'typeOfTrade', v)
                            }}
                            freeSolo
                            loading={tradeTypeLookup.isLoading}
                            renderInput={(params) => <TextField {...params} placeholder="Type of Trade" sx={massInputSx} />}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 80 }} onPasteCapture={(e) => handleMassPaste(index, 5, e)}>
                          <TextField
                            size="small"
                            type="number"
                            value={row.lot}
                            onChange={(e) => updateMassRow(index, 'lot', e.target.value)}
                            onBlur={(e) => updateMassRow(index, 'lot', normalizeMassNumber(e.target.value))}
                            fullWidth
                            inputProps={{ inputMode: 'decimal', step: 'any', style: { textAlign: 'right' } }}
                            sx={massInputSx}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 120 }} onPasteCapture={(e) => handleMassPaste(index, 6, e)}>
                          <Autocomplete
                            size="small"
                            options={partnerOptions}
                            value={row.partner || null}
                            onChange={(_, v) => updateMassRow(index, 'partner', v ?? '')}
                            onInputChange={(_, v, reason) => {
                              if (reason === 'input') updateMassRow(index, 'partner', v)
                            }}
                            freeSolo
                            loading={partnerLookup.isLoading}
                            renderInput={(params) => <TextField {...params} placeholder="Partner" sx={massInputSx} />}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 120 }} onPasteCapture={(e) => handleMassPaste(index, 7, e)}>
                          <Autocomplete
                            size="small"
                            options={flagOptions}
                            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.flag)}
                            isOptionEqualToValue={(opt, v) =>
                              (typeof opt === 'string' ? opt : opt.flag) === (typeof v === 'string' ? v : v?.flag)
                            }
                            value={row.flag ? flagOptions.find((o) => o.flag === row.flag) ?? { flag: row.flag, description: '' } : null}
                            onChange={(_, v) => updateMassRow(index, 'flag', typeof v === 'string' ? v : v?.flag ?? '')}
                            onInputChange={(_, v, reason) => {
                              if (reason === 'input') updateMassRow(index, 'flag', v)
                            }}
                            freeSolo
                            loading={flagLookup.isLoading}
                            renderOption={(props, opt) => (
                              <li {...props} key={typeof opt === 'string' ? opt : opt.flag}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{typeof opt === 'string' ? opt : opt.flag}</Typography>
                                  {typeof opt !== 'string' && opt.description ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                      {opt.description}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </li>
                            )}
                            renderInput={(params) => <TextField {...params} placeholder="Flag" sx={massInputSx} />}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 120 }} onPasteCapture={(e) => handleMassPaste(index, 8, e)}>
                          <TextField
                            size="small"
                            type="number"
                            value={row.budgetedHedge}
                            onChange={(e) => updateMassRow(index, 'budgetedHedge', e.target.value)}
                            onBlur={(e) => updateMassRow(index, 'budgetedHedge', normalizeMassNumber(e.target.value))}
                            fullWidth
                            inputProps={{ inputMode: 'decimal', step: 'any', style: { textAlign: 'right' } }}
                            sx={massInputSx}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 160 }} onPasteCapture={(e) => handleMassPaste(index, 9, e)}>
                          <TextField
                            size="small"
                            value={row.note}
                            onChange={(e) => updateMassRow(index, 'note', e.target.value)}
                            fullWidth
                            multiline
                            maxRows={4}
                            sx={massInputSx}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, width: 44, verticalAlign: 'middle' }}>
                          <Tooltip title="Delete row">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={massRows.length === 1}
                                onClick={() => {
                                  setMassRows((current) => current.filter((_, idx) => idx !== index))
                                  setMassSelected((current) => {
                                    const next = new Set<number>()
                                    current.forEach((i) => {
                                      if (i === index) return
                                      next.add(i > index ? i - 1 : i)
                                    })
                                    return next
                                  })
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}
